import { MatterSelectDropdown } from '@/components/matter-select'
import useAssignConversationMatter from '@/matter/hooks/useAssignConversationMatter'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { selectMoveChatDestinationsMattersSortedByName } from '../store/selectors'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType, kToastDurationMediumMs } from '@/constants/constants-ui'
import { kUntitledMatterLabel } from '../constants'
import { useState } from 'react'
import CreateMatterDialog from './matter-dialogs/CreateMatterDialog'
import { CreateOrUpdateMatterForm } from '../store/schemas'
import { buildConversationPath } from '@/chat-common/conversation-navigation/util/conversation-path-builders'
import { RootState } from '@/store/store'
import { setMobileChatHistoryOpen } from '@/store/slices/ui-state.slice'
import { DEVICE_TYPE, useDeviceType } from '@/hooks/useDeviceType'
import { MatterCreationError } from '@/matter/errors'
import { useConversationNavigation } from '@/chat-common/conversation-navigation/hooks/useConversationNavigation'

type MatterBoundInterceptionProps = {
  conversationId: string
}

/**
 * Matter Bound Interception
 *
 * This component is used to intercept conversations that are not assigned to a matter and display a warning and a dropdown to assign the conversation to a matter.
 *
 * @param conversationId - The ID of the conversation to intercept
 * @returns The Matter Bound Interception component
 */
export default function MatterBoundInterception({ conversationId }: MatterBoundInterceptionProps) {
  const { assignMatter, assignToNewMatter } = useAssignConversationMatter({ conversationId })
  const { navigateToConversationByMatter } = useConversationNavigation()
  const matters = useAppSelector(selectMoveChatDestinationsMattersSortedByName)
  const deviceType = useDeviceType()
  const isMobile = deviceType === DEVICE_TYPE.MOBILE

  const currentMatterId = useAppSelector((state: RootState) => state.mattersState.activeMatterId)

  const dispatch = useAppDispatch()

  // Local state for the create matter dialog
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)

  // Handle the assignment of the matter (same pattern as ChatOverflowMenu)
  const handleMatterAssignment = async (toMatterId: string) => {
    const matterName = matters.find((m) => m.id === toMatterId)?.name ?? kUntitledMatterLabel

    try {
      await assignMatter(toMatterId, () => {
        // Close the chat list sidebar on mobile
        if (isMobile) {
          dispatch(setMobileChatHistoryOpen(false))
        }
        dispatch(
          openGlobalToast({
            type: GlobalToastType.SUCCESS,
            message: `Conversation moved to ${matterName}`,
            durationMs: kToastDurationMediumMs,
            withLink: { label: 'Go to Chat', href: buildConversationPath(toMatterId, conversationId) },
            truncateAtLine: 2,
          })
        )
        // Navigate away since the conversation being viewed is no longer in the current matter
        if (currentMatterId) {
          navigateToConversationByMatter({ matterId: currentMatterId })
        }
      })
    } catch (err) {
      console.error('Error while moving conversation:', err)
      dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message: 'Error while moving conversation', durationMs: kToastDurationMediumMs }))
    }
  }

  // Handle creating a new matter and assigning the conversation to it
  const handleCreateMatterSubmit = async (matterData: CreateOrUpdateMatterForm) => {
    try {
      await assignToNewMatter(matterData, (newMatter) => {
        // Close the chat list sidebar on mobile
        if (isMobile) {
          dispatch(setMobileChatHistoryOpen(false))
        }
        dispatch(
          openGlobalToast({
            type: GlobalToastType.SUCCESS,
            message: `Created matter "${newMatter.name}" and assigned conversation`,
            durationMs: kToastDurationMediumMs,
            withLink: { label: 'Go to Chat', href: buildConversationPath(newMatter.id, conversationId) },
            truncateAtLine: 2,
          })
        )
        // Navigate away since the conversation being viewed is no longer in the current matter
        if (currentMatterId) {
          navigateToConversationByMatter({ matterId: currentMatterId })
        }
      })
    } catch (err) {
      console.error('Error creating matter and assigning conversation:', err)

      // Matter creation failed - re-throw so CreateMatterDialog can display the error in the form
      if (err instanceof MatterCreationError) {
        throw err.cause
      }

      // Assignment failed (or any other error) - show toast since the matter creation succeeded
      dispatch(
        openGlobalToast({ type: GlobalToastType.ERROR, message: 'Matter created, but failed to assign conversation', durationMs: kToastDurationMediumMs })
      )
      setIsCreateDialogOpen(false)
    }
  }

  return (
    <>
      <div className="relative w-full max-w-3xl m-auto border-t py-5">
        <div className="w-full self-stretch min-h-28 p-3 outline outline-1 outline-brand-neutral-200 rounded-lg inline-flex flex-col justify-center items-center gap-1 bg-white">
          <p className="text-sm text-brand-neutral-600 self-stretch text-center">This chat must be assigned to a matter before you can continue.</p>
          <div className="flex justify-center">
            <MatterSelectDropdown
              arrowIcon="ChevronDown"
              items={matters}
              onCreateNewMatter={() => setIsCreateDialogOpen(true)}
              onSelect={handleMatterAssignment}
              placeholder="Assign to Matter"
              selectedItemId={null}
            />
          </div>
        </div>
      </div>

      <CreateMatterDialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen} onSubmit={handleCreateMatterSubmit} />
    </>
  )
}
